/**
 * Slot resolution.
 *
 * `View.slots` maps a slot name to "a field path or template over a row". That is
 * the SAME grammar bindings use, so this file resolves it with the binding
 * runtime's own `interpolate`/`getPath` rather than growing a second, subtly
 * different resolver that drifts from the first one after the third bug report.
 */
import { getPath, interpolate } from '@clawharbor/binding-runtime';
import type { Row } from './types.js';

/** A row is the template scope. `row`/`item` aliases let slots be explicit. */
export function toScope(row: unknown): Record<string, unknown> {
  const base = row != null && typeof row === 'object' && !Array.isArray(row) ? (row as Row) : { value: row };
  return { ...base, row: base, item: base };
}

/**
 * A spec containing `{{` is a template; anything else is a bare field path.
 * Template resolution can throw (unknown filter in a generated manifest), and a
 * bad slot must degrade to a missing line, never to a blank surface.
 */
export function resolveSlot(spec: string | undefined, row: unknown): unknown {
  if (!spec) return undefined;
  try {
    if (spec.includes('{{')) return interpolate(spec, toScope(row));
    return getPath(toScope(row), spec);
  } catch {
    return undefined;
  }
}

/** Resolved slot as display text, or undefined when there is nothing to show. */
export function slotText(spec: string | undefined, row: unknown, labels?: Record<string, string>): string | undefined {
  const v = resolveSlot(spec, row);
  const s = stringify(v, labels);
  return s === '' ? undefined : s;
}

export function stringify(v: unknown, labels?: Record<string, string>): string {
  if (v == null) return '';
  if (Array.isArray(v)) {
    return v
      .map((x) => stringify(x, labels))
      .filter(Boolean)
      .join(', ');
  }
  if (typeof v === 'boolean') return v ? 'yes' : '';
  if (typeof v === 'object') return '';
  const s = String(v).trim();
  return labels?.[s] ?? s;
}

/**
 * Only http(s) links leave this renderer.
 *
 * Rows are built from third-party HTML we did not write; an `href` extracted from
 * a page is attacker-influenced input by construction, so `javascript:` and
 * `data:` are dropped rather than sanitised. Relative hrefs resolve against the
 * tenant's canonical origin, which is also what keeps deepLinkOnly honest.
 */
export function safeHref(value: unknown, origin?: string): string | null {
  if (typeof value !== 'string') return null;
  const raw = value.trim();
  if (!raw) return null;
  try {
    const url = origin ? new URL(raw, origin) : new URL(raw);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
    return url.toString();
  } catch {
    return null;
  }
}

export type BadgeTone = 'default' | 'muted' | 'premium' | 'danger';

export interface Badge {
  label: string;
  tone: BadgeTone;
  /** The field the badge came from, for the accessible label. */
  field: string;
}

const DANGER_FIELD = /(sold.?out|disabled|unavailable|cancell?ed|closed|error)/i;
const PREMIUM_VALUE = /(gold|vip|premium|imax|4dx|dolby|screenx|deluxe|lux)/i;
const MUTED_FIELD = /(note|info|lang|language|audio)/i;

/**
 * Badges are the densest thing on the card, so an empty or false field must
 * produce nothing at all rather than a chip reading "false" or "-".
 */
export function badgesFor(fields: string[], row: unknown, labels?: Record<string, string>): Badge[] {
  const out: Badge[] = [];
  for (const field of fields) {
    const raw = resolveSlot(field, row);
    if (raw == null || raw === false || raw === '') continue;

    // A true boolean means "this flag applies"; the field name is the label.
    const values: unknown[] =
      raw === true ? [humanize(lastSegment(field))] : Array.isArray(raw) ? raw : [raw];

    for (const v of values) {
      const label = stringify(v, labels);
      if (!label) continue;
      out.push({ label, tone: toneFor(field, label), field });
    }
  }
  return out;
}

function toneFor(field: string, label: string): BadgeTone {
  if (DANGER_FIELD.test(field)) return 'danger';
  if (PREMIUM_VALUE.test(label)) return 'premium';
  if (MUTED_FIELD.test(field)) return 'muted';
  return 'default';
}

export function lastSegment(path: string): string {
  const cleaned = path.replace(/\{\{|\}\}/g, '').split('|')[0]!.trim();
  const parts = cleaned.split('.');
  return parts[parts.length - 1] ?? cleaned;
}

/** `sold_out` / `soldOut` / `sold-out` → `Sold out`. */
export function humanize(key: string): string {
  const words = key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_\-.]+/g, ' ')
    .trim()
    .toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * Flatten `Manifest.domain.vocabularies` into the token → label map the renderer
 * wants. Site jargon (CineStar's `TITL` for "Subtitled") is exactly the thing an
 * agent cannot guess and a badge must not show raw.
 */
export function flattenVocabularies(
  vocabularies: Record<string, { tokens?: Record<string, Record<string, string>> }> | undefined,
  locale = 'en',
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const vocab of Object.values(vocabularies ?? {})) {
    for (const [token, byLocale] of Object.entries(vocab.tokens ?? {})) {
      const label = byLocale[locale] ?? byLocale['en'] ?? Object.values(byLocale)[0];
      if (label) out[token] = label;
    }
  }
  return out;
}

/**
 * Group by a field path, preserving first-seen order.
 *
 * Generic over the carried item because the renderer groups `{row, index}` pairs
 * (the index is what `onSelect` hands back to the WebMCP page) while everything
 * else groups bare rows.
 */
export function groupBy<T>(
  items: T[],
  path: string | undefined,
  rowOf: (item: T) => unknown,
): Array<{ key: string; items: T[] }> {
  if (!path) return [{ key: '', items }];
  const order: string[] = [];
  const map = new Map<string, T[]>();
  for (const item of items) {
    const key = stringify(resolveSlot(path, rowOf(item))) || '—';
    let bucket = map.get(key);
    if (!bucket) {
      bucket = [];
      map.set(key, bucket);
      order.push(key);
    }
    bucket.push(item);
  }
  return order.map((key) => ({ key, items: map.get(key)! }));
}

/** Group bare rows. */
export function groupRows(rows: Row[], path: string | undefined): Array<{ key: string; rows: Row[] }> {
  return groupBy(rows, path, (r) => r).map(({ key, items }) => ({ key, rows: items }));
}

/** `20:15` or an ISO instant → a valid `<time datetime>`; otherwise null. */
export function datetimeAttr(value: string | undefined): string | null {
  if (!value) return null;
  const s = value.trim();
  if (/^\d{1,2}:\d{2}(:\d{2})?$/.test(s)) return s.padStart(5, '0');
  if (/^\d{4}-\d{2}-\d{2}([T ]\d{2}:\d{2}(:\d{2})?)?/.test(s)) return s.replace(' ', 'T');
  return null;
}

export function asRows(rows: unknown[]): Row[] {
  return rows.map((r) => (r != null && typeof r === 'object' && !Array.isArray(r) ? (r as Row) : { value: r }));
}
